import FlashRoot from '../FlashMessage/FlashRoot';
import FontAwesome from 'react-fontawesome';
import Placeholder from '../Placeholder';
import React, { useState } from 'react';
import ValidationErrors from '../forms/ValidationErrors';
import classnames from 'classnames';
import duckify from '../../utils/duckify';
import flashMessages from '../../ducks/flashMessages';
import PageHeader from '../PageHeader';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Formik, Field, Form } from 'formik';
import { titleize } from '../../utils';
import { veracrossConfig as veracrossConfigDuck } from '../../ducks';

import './styles/VeracrossConfig.scss';
import SecretsInput from '../shared/SecretsInput';

/**
 * Veracross integration setup component.
 */
const VeracrossConfig = ({ duckMeta, onSave, onTest, showFlash, veracrossConfig }) => {
  const [errors, setErrors] = useState({});
  const [testError, setTestError] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [showScopes, setShowScopes] = useState(false);

  const reset = () => {
    setErrors({});
    setTestError(null);
    setTestLoading(false);
  };

  const handleSubmit = (values, { setSubmitting }) => {
    reset();

    onSave(values)
      .then(() => showFlash({ message: 'Veracross configuration saved.' }))
      .catch(response => {
        if (response.veracross_config) {
          setErrors(response.veracross_config.errors);
        } else {
          showFlash({ message: 'An unexpected error occurred.', type: 'danger' });
        }
      })
      .then(() => setSubmitting(false));
  };

  const handleTest = () => {
    reset();
    setTestLoading(true);

    onTest()
      .then(() => showFlash({ message: 'Credentials test successful.' }))
      .catch(response => setTestError(response))
      .then(() => setTestLoading(false));
  };

  return (<React.Fragment>
    <PageHeader header='Veracross Integration Setup'/>

    <FlashRoot/>
    <ValidationErrors errors={errors}/>

    {testError && <div className="alert alert-danger">
      Error: {testError.message}
    </div>}

    <Placeholder.loading ready={duckMeta.veracrossConfig.ready}>
      <Formik enableReinitialize={true} initialValues={veracrossConfig} onSubmit={handleSubmit}>
        {({ dirty, isSubmitting, setFieldValue }) => {
          return (
            <Form className="veracross-config-form">
              <div className="base-setup-fields">
                <div className="base-setup-field-group">
                  <div className="base-setup-field-checkbox-value">
                    <Field type="checkbox" name="enabled" id="enabled"/>
                  </div>
                  <label className="base-setup-field-checkbox-label" htmlFor="enabled">
                    Enable Veracross Integration
                  </label>
                </div>

                <div className="base-setup-field-group">
                  <label className={classnames("base-setup-field-label", { error: errors.school_route })}>
                    School Route
                  </label>
                  <div className="base-setup-field-value">
                    <Field type="text" name="school_route" className="base-setup-field-input" autoComplete="new-password"/>
                  </div>
                </div>

                <div className="base-setup-field-group">
                  <label className={classnames("base-setup-field-label", { error: errors.client_id })}>
                    Client ID
                  </label>
                  <div className="base-setup-field-value">
                    <Field type="text" name="client_id" className="base-setup-field-input" autoComplete="new-password"/>
                  </div>
                </div>

                <div className="base-setup-field-group">
                  <label className={classnames("base-setup-field-label", { error: errors.client_secret })}>
                    Client Secret
                  </label>
                  <div className="base-setup-field-value">
                    <Field as={SecretsInput}
                           key={veracrossConfig.client_secret}
                           name="client_secret"
                           className="base-setup-field-input"
                           onCancel={el => setFieldValue(el.name, veracrossConfig[el.name])}
                    />
                  </div>
                </div>

                <div className="base-setup-field-group">
                  <label className={classnames("base-setup-field-label", { error: errors.pending_status_id })}>
                    Pending Application Status ID
                    <OverlayTrigger overlay={
                      <Tooltip>
                        <p>
                          The application status ID for the <em>Pending</em> status. The exporter will automatically
                          put applications into this status before moving them to <em>Accepted</em>.
                        </p>
                      </Tooltip>
                    }>
                      <FontAwesome name="info-circle" className="ms-2"/>
                    </OverlayTrigger>
                  </label>
                  <div className="base-setup-field-value">
                    <Field type="number"
                           name="pending_status_id"
                           min="0"
                           className="base-setup-field-input"
                           autoComplete="off"/>
                  </div>
                </div>

                <Button type="submit" disabled={isSubmitting}>Save</Button>

                {veracrossConfig.enabled && <div className="veracross-config-test">
                  {!dirty && <Button disabled={isSubmitting || testLoading || dirty}
                                     onClick={handleTest}>
                    {testLoading ? 'Testing...' : 'Test'}
                  </Button>}

                  {dirty && <span>Save changes to test</span>}
                </div>}
              </div>
            </Form>
          );
        }}
      </Formik>

      {veracrossConfig.scopes && <React.Fragment>
        <h2>Scopes</h2>
        <p>Permission scopes that Finalsite Enrollment requires for its client credentials.</p>
        <p>
          <Button onClick={() => setShowScopes(!showScopes)}>
            <FontAwesome name={showScopes ? 'caret-down' : 'caret-right'}/>
            {' '}{showScopes ? 'hide' : 'show'} scopes
          </Button>
        </p>
        {showScopes && <pre>{veracrossConfig.scopes.join('\n')}</pre>}
      </React.Fragment>}

      {veracrossConfig.enabled && <React.Fragment>
        <h2>Value Lists</h2>
        <p>Download value lists for these Veracross entities:</p>
        <ul className="veracross-config-value-lists">
          {veracrossConfig.value_list_types.map(type => (
            <li key={type}>
              <a href={`/api/veracross_config/value_lists?type=${type}`}>{titleize(type)}</a>
            </li>
          ))}
        </ul>
      </React.Fragment>}
    </Placeholder.loading>
  </React.Fragment>);
};

const mapDucksToProps = () => ({
  veracrossConfig: veracrossConfigDuck
});

const mapStateToProps = () => {
};

const mapDispatchToProps = (dispatch, _props) => ({
  onSave: (config) => dispatch(veracrossConfigDuck.actions.save(config)),
  onTest: () => dispatch(veracrossConfigDuck.actions.testCredentials()),
  showFlash: (props) => dispatch(flashMessages.actions.show(props))
});

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(VeracrossConfig);
